import './quick-access-login.screen.scss';
import {Typography, Avatar, Box, Button, SwipeableDrawer, Divider, Alert} from '@mui/material';
import {useContext, useEffect, useLayoutEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {User as FirebaseUser} from 'firebase/auth';
import {ScreenPaths} from '../../../enums/screen-path';
import {translations} from '../../../i18n/translations';
import LoginIcon from '@mui/icons-material/Login';
import ReplayIcon from '@mui/icons-material/Replay';
import {ProductLogoWithStick} from '../../../components/product-logo-with-stick/product-logo-with-stick';
import {AppContextType} from '../../../contexts/app/types';
import {AppContext} from '../../../contexts/app/context';
import {QuickAccessInfo, QuickAccessManager} from '../../../managers/quick-access-manager';
import {LoadingContent} from '../../../components/loading-content/loading-content';
import {QuickAccessData} from '../../../interfaces/quick-access-data';
import {UserQuickSignInInfo} from '../../../interfaces/user-quick-sign-in-info';
import {AuthContextType} from '../../../contexts/auth/types';
import {AuthContext} from '../../../contexts/auth/context';
import {LegalLinksStack} from '../../../components/legal-links-stack/legal-links-stack';
import QuickAccessLoginPinDrawer from './quick-access-login-pin-drawer';
import UserAvatarButton from './user-avatar-button';
import {MainDatabase} from '../../../databases/main';
import {SessionDatabase} from '../../../databases/session';
import {exitToSocialApp} from '../../../utils/social-app/social-app-launch';

export default function QuickAccessLoginScreen() {
  const appContext: AppContextType = useContext(AppContext);
  const authContext: AuthContextType = useContext(AuthContext);
  const {t} = useTranslation();
  let history = useHistory();
  const location = useLocation();

  // Tool > Get current view mode
  const getCurrentViewMode = (): 'portrait' | 'landscape' => {
    return window.innerWidth < window.innerHeight ? 'portrait' : 'landscape';
  };

  // Hold state
  const [currentViewMode, setCurrentViewMode] = useState<'portrait' | 'landscape'>(
    getCurrentViewMode()
  );
  const [data, setData] = useState<QuickAccessData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [info, setInfo] = useState<QuickAccessInfo | null>(null);
  const [usersListMode, setUsersListMode] = useState<'available' | 'add'>('available');
  const [selectedUser, setSelectedUser] = useState<UserQuickSignInInfo | null>(null);
  const [codeDrawerState, setCodeDrawerState] = useState<'ready' | 'loading' | 'error' | string[]>(
    'ready'
  );

  // Navigate to the login screen
  const handleNavigateToLogin = () => {
    // Disable Quick Access Login
    QuickAccessManager.instance.disable();

    // Hold where to go
    let pathname: string = ScreenPaths.Login;

    // Get last part of the pathname
    const pathnameParts = location.pathname.split('/');
    const lastPart = pathnameParts[pathnameParts.length - 1];

    // Check previous path
    if (lastPart === 'daily-care-log') {
      pathname = ScreenPaths.LoginDailyCareLog;
    }

    // Navigate to the login screen
    history.push({
      pathname,
    });
  };

  // Listen for resize changes
  useLayoutEffect(() => {
    // Define action
    function updateSize() {
      // Update the current view mode
      setCurrentViewMode(getCurrentViewMode());
    }

    // Add listener
    window.addEventListener('resize', updateSize);

    // First call
    updateSize();

    // On unmount
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Listen for Selected User changes
  useEffect(() => {
    // Since selected user changed
    setCodeDrawerState('ready');
  }, [selectedUser]);

  // On mount
  useEffect(() => {
    // Clear Databases
    MainDatabase.instance.clearAllData();
    SessionDatabase.instance.clearAllData();

    // Fetch data
    QuickAccessManager.instance.data().then(setData);

    // Once here, let's make sure that Quick Access Mode stays enabled
    QuickAccessManager.instance.enable();

    // Reset states
    setIsLoading(true);
    setInfo(null);

    // Get Data
    QuickAccessManager.instance
      .getInfo()
      .then((data: QuickAccessInfo | null) => {
        setInfo(data);
        setIsLoading(false);
      })
      .catch((error) => setIsLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build
  return (
    <>
      <Box className="quick-access-login-container">
        <Box className="panel">
          <Box
            className="header"
            sx={{
              flexDirection: 'column',
            }}
          >
            <Box className="sioslife">
              <ProductLogoWithStick
                height={appContext.state.currentThemeSettings.loginLogoStickHeight}
              />
              <Typography variant="h6" className="login-screen-title">
                {t(translations.login.welcome)}
              </Typography>
            </Box>
            {!isLoading && info && (
              <Box className="entity">
                <Box className="entity-container">
                  <Avatar className="avatar" alt={info.entity.name} src={info.entity.picture} />
                  <Typography className="name">{info.entity.name}</Typography>
                </Box>
              </Box>
            )}
          </Box>
          <Box className="body">
            {!isLoading && info && (
              <Typography className="login-screen-description">
                {t(translations.quick_access_login.instruction)}
              </Typography>
            )}
            {!isLoading && info && usersListMode === 'available' && (
              <Box
                className={
                  !data || !data.userIds || (data?.userIds && data.userIds.length === 0)
                    ? 'user-list-empty'
                    : 'users-list'
                }
              >
                <UserAvatarButton name="+" onClick={() => setUsersListMode('add')} />
                {data &&
                  data.userIds &&
                  data.userIds.length > 0 &&
                  data.userIds.map((userId) => {
                    // Look for user Quick Sign Info
                    const userQuickSignInInfo = info.quickSignInUsers?.find(
                      (user) => user.id === userId
                    );

                    // Eval
                    if (!userQuickSignInInfo) {
                      return null;
                    }

                    // Build
                    return (
                      <UserAvatarButton
                        key={userId}
                        name={userQuickSignInInfo.name}
                        pictureUrl={userQuickSignInInfo.picture}
                        onClick={() => setSelectedUser(userQuickSignInInfo)}
                      />
                    );
                  })}
              </Box>
            )}
            {!isLoading && info && usersListMode === 'add' && (
              <Box
                className={
                  !info.quickSignInUsers ||
                  (info?.quickSignInUsers && info.quickSignInUsers.length === 0)
                    ? 'user-list-empty'
                    : 'users-list'
                }
              >
                {info.quickSignInUsers &&
                  info.quickSignInUsers.length > 0 &&
                  info.quickSignInUsers.map((userQuickSignInInfo) => {
                    // Build
                    return (
                      <UserAvatarButton
                        key={userQuickSignInInfo.id}
                        name={userQuickSignInInfo.name}
                        pictureUrl={userQuickSignInInfo.picture}
                        onClick={() => setSelectedUser(userQuickSignInInfo)}
                      />
                    );
                  })}
              </Box>
            )}
            {!isLoading && info && usersListMode === 'add' && (
              <>
                <Divider className="divider" />
                <Button
                  variant="text"
                  onClick={() => setUsersListMode('available')}
                  className="button-text"
                  sx={{
                    color: 'var(--color-gray)',
                    marginTop: '20px',
                    marginBottom: '20px',
                  }}
                >
                  {t(translations.quick_access_login.go_back_to_the_recent_users_list)}
                </Button>
              </>
            )}
            {isLoading && (
              <Box className="users-list">
                <LoadingContent sx={{gridColumnStart: 1, gridColumnEnd: -1}} />
              </Box>
            )}
            {!isLoading && !info && (
              <Box className="users-list-error">
                <Alert severity="error">
                  <Trans>{t(translations.quick_access_login.error)}</Trans>
                </Alert>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  startIcon={<ReplayIcon />}
                >
                  {t(translations.quick_access_login.try_again)}
                </Button>
              </Box>
            )}

            {usersListMode === 'available' && (
              <>
                <Divider
                  className="divider"
                  sx={{
                    marginTop: '16px',
                  }}
                />
                <Box className="back-to-email-and-password-login-button-container">
                  <Button
                    variant="text"
                    className="button-text"
                    onClick={handleNavigateToLogin}
                    startIcon={<LoginIcon />}
                  >
                    {t(translations.quick_access_login.back_to_email_and_password_login)}
                  </Button>

                  {appContext.state.runningFromSocialApp && (
                    <Button
                      sx={{
                        width: '65%',
                        marginTop: '12px !important',
                        boxShadow: 'inset 0 0 0 4px rgba(255, 255, 255, 0.6)',
                      }}
                      variant="contained"
                      color="error"
                      size="large"
                      onClick={() => exitToSocialApp()}
                    >
                      {t(translations.office.generics.exit)}
                    </Button>
                  )}
                </Box>

                <LegalLinksStack />
              </>
            )}
          </Box>
        </Box>
      </Box>
      <SwipeableDrawer
        className="quick-access-login-pin-drawer"
        anchor={currentViewMode === 'portrait' ? 'bottom' : 'right'}
        open={selectedUser !== null}
        onClose={() => {
          if (codeDrawerState !== 'loading') setSelectedUser(null);
        }}
        onOpen={() => {}}
      >
        <Box className="content">
          <Box className="inner-content">
            {selectedUser && (
              <QuickAccessLoginPinDrawer
                selectedUser={selectedUser}
                state={codeDrawerState}
                onSubmit={async (pin?: string) => {
                  setCodeDrawerState('loading');

                  if (pin) {
                    const answer = await QuickAccessManager.instance.getUserToken(
                      selectedUser.id,
                      pin
                    );

                    if (typeof answer !== 'string') {
                      setCodeDrawerState(answer);
                    } else if (typeof answer === 'string') {
                      // Remember user
                      setData(await QuickAccessManager.instance.rememberUser(selectedUser.id));
                      // saveUserAndToken
                      QuickAccessManager.instance.writeQuickAccessUserData(selectedUser.id, answer);

                      // Restore with Quick Access
                      authContext.restorePreviousSessionByQuickAccess(answer, {
                        uid: selectedUser.id,
                        photoURL: selectedUser.picture !== undefined ? selectedUser.picture : null,
                        displayName: selectedUser.name,
                      } as FirebaseUser);
                    }
                  } else {
                    setCodeDrawerState('ready');
                  }
                }}
                onRemoveUser={async (userId: string) => {
                  setData(await QuickAccessManager.instance.forgetUser(userId));
                  setSelectedUser(null);
                }}
              />
            )}
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
}
